import {
  CheckboxSwitch,
  CircleLoader,
  LoaderSizes,
  StandardPagination,
  brandfolderColorPalette
} from '@brandfolder/react';
import { Trans, t } from '@lingui/macro';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import { amplitudeService } from '../../../services/amplitude';

import { PeopleFilterType, PeoplePage, PeopleTaggingBackfillState } from '@components/people_tags/customPeopleTagTypes';
import { getStandardPaginationLabels } from '@translations';

import { useFetchPeopleHubData } from './hooks/useFetchPeopleHubData';
import { PeopleManagementActionsBar } from './PeopleManagementActionsBar';
import { PeopleManagementProvider, usePeopleManagementContext } from './PeopleManagementContext';
import { PeopleManagementFilter } from './PeopleManagementFilter';
import { PeoplePageHeader } from './PeoplePageHeader';
import {
  NoFacesEmptyState,
  PeopleTaggingStatusScreen
} from './PeopleTaggingStatusScreen';
import { PersonCard } from './PersonCard';

const HiddenPeopleEmptyStateWrapper = styled.div`
  margin: 0 auto;
  max-width: 480px;
  padding: 64px 0;
  text-align: center;

  p {
    font-weight: 450;
    font-size: 14px;
    line-height: 20px;
    color: ${brandfolderColorPalette.neutrals[500]};
  }
`;

const PeopleTagsFaceCardsWrapper = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(1, 100%);
  justify-content: center;

  @media (min-width: 320px) {
    grid-template-columns: repeat(2, 48%);
  }
  @media (min-width: 744px) {
    grid-template-columns: repeat(2, minmax(166px, 1fr));
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(166px, 1fr));
  }
  @media (min-width: 1320px) {
    grid-template-columns: repeat(4, minmax(194px, 1fr));
  }
  @media (min-width: 1526px) {
    grid-template-columns: repeat(5, minmax(229px, 1fr));
  }
  @media (min-width: 1728px) {
    grid-template-columns: repeat(6, minmax(15%, 1fr));
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 40px;
  padding: 0 20px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${brandfolderColorPalette.neutrals[500]};
  font-weight: 450;
  line-height: 20px;
`;

const BottomPaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  margin-bottom: 40px;
`;

const ITEMS_PER_PAGE = 24;

const PEOPLE_HUB_ENTRY_POINT_STORAGE_KEY = 'bf_people_hub_entry_point';

type PeopleHubEntryPoint = 'sidebar_nav' | 'admin_settings' | 'breadcrumb' | 'direct_url';

function searchExecuteFilterType(
  filter: PeopleFilterType,
  appliedSearchTrimmed: string
): 'all' | 'named_only' | 'search' | 'unnamed_only' {
  if (appliedSearchTrimmed.length > 0) {
    return 'search';
  }
  switch (filter) {
    case PeopleFilterType.Named:
      return 'named_only';
    case PeopleFilterType.Unnamed:
      return 'unnamed_only';
    default:
      return 'all';
  }
}

function readPeopleHubEntryPoint(): PeopleHubEntryPoint {
  try {
    const raw = sessionStorage.getItem(PEOPLE_HUB_ENTRY_POINT_STORAGE_KEY);
    sessionStorage.removeItem(PEOPLE_HUB_ENTRY_POINT_STORAGE_KEY);
    if (
      raw === 'sidebar_nav' ||
      raw === 'admin_settings' ||
      raw === 'breadcrumb' ||
      raw === 'direct_url'
    ) {
      return raw;
    }
  } catch {
    // ignore storage errors
  }
  return 'direct_url';
}

interface PaginationBarProps {
  paginationText: string;
  showHiddenPeople: boolean;
  setShowHiddenPeople: (show: boolean) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationBar: FunctionComponent<PaginationBarProps> = ({
  paginationText,
  showHiddenPeople,
  setShowHiddenPeople,
  currentPage,
  totalPages,
  onPageChange
}) => (
  <PaginationWrapper>
    <PaginationInfo>{paginationText}</PaginationInfo>
    <PaginationControls>
      <CheckboxSwitch
        defaultChecked={showHiddenPeople}
        id="show-hidden-people-toggle"
        label={t`Show hidden people`}
        onChange={(): void => setShowHiddenPeople(!showHiddenPeople)}
      />
      <StandardPagination
        initial={currentPage}
        labels={getStandardPaginationLabels()}
        onPageChange={onPageChange}
        total={totalPages}
      />
    </PaginationControls>
  </PaginationWrapper>
);

interface PeopleManagementContentProps {
  initialLoadingState: boolean;
  peopleData: PeoplePage[];
  peopleDataLoading: boolean;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalCount: number;
  onLocalMerge?: (
    sourceId: string,
    targetPerson: { person_id: string; name: string; thumbnail_url: string; facesMoved?: number }
  ) => void;
  onLocalNameUpdate?: (personId: string, newName: string) => void;
  showHiddenPeople: boolean;
  setShowHiddenPeople: (show: boolean) => void;
}

const PeopleManagementContent: FunctionComponent<PeopleManagementContentProps> = ({
  initialLoadingState,
  peopleData,
  peopleDataLoading,
  totalPages,
  currentPage,
  onPageChange,
  totalCount,
  onLocalMerge = undefined,
  onLocalNameUpdate = undefined,
  showHiddenPeople,
  setShowHiddenPeople
}) => {
  const showPageLoader = initialLoadingState || peopleDataLoading;

  const allPeopleHiddenOnPage = peopleData?.length === 0 && totalCount > 0;

  if (showPageLoader) {
    return <CircleLoader label="people-data" size={LoaderSizes.Large} />;
  }

  // Show message when faces exist but all are hidden and toggle is off
  if (allPeopleHiddenOnPage && !showHiddenPeople) {
    return (
      <>
        <PaginationBar
          currentPage={currentPage}
          onPageChange={onPageChange}
          paginationText={t`0 people`}
          setShowHiddenPeople={setShowHiddenPeople}
          showHiddenPeople={showHiddenPeople}
          totalPages={totalPages}
        />
        <HiddenPeopleEmptyStateWrapper>
          <p>
            <Trans>
              Faces have been detected, but they&apos;re currently hidden.
              Turn on <strong>Show hidden people</strong> to view and manage these identities.
            </Trans>
          </p>
        </HiddenPeopleEmptyStateWrapper>
      </>
    );
  }

  // Calculate pagination info based on current page data
  const displayCount = peopleData.length;
  const startItem = displayCount > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const endItem = displayCount > 0 ? startItem + displayCount - 1 : 0;

  const paginationText = displayCount > 0
    ? t`${startItem}–${endItem} of ${totalCount} ${totalCount === 1 ? 'person' : 'people'}`
    : t`0 people`;

  return (
    <>
      <PaginationBar
        currentPage={currentPage}
        onPageChange={onPageChange}
        paginationText={paginationText}
        setShowHiddenPeople={setShowHiddenPeople}
        showHiddenPeople={showHiddenPeople}
        totalPages={totalPages}
      />
      <PeopleTagsFaceCardsWrapper>
        {peopleData.map((faceCard) => (
          <PersonCard
            key={faceCard.person_id}
            faceCount={faceCard.face_count}
            hidden={faceCard.isHidden}
            imageUrl={faceCard.thumbnail_url}
            name={faceCard.name}
            onLocalMerge={onLocalMerge}
            onLocalNameUpdate={onLocalNameUpdate}
            personId={faceCard.person_id}
          />
        ))}
      </PeopleTagsFaceCardsWrapper>
      <BottomPaginationWrapper>
        <StandardPagination
          initial={currentPage}
          labels={getStandardPaginationLabels()}
          onPageChange={onPageChange}
          total={totalPages}
        />
      </BottomPaginationWrapper>
    </>
  );
};

function getPeopleTaggingUiState(): PeopleTaggingBackfillState {
  return BFG.brandfolderSettings?.people_tagging_ui_state
    ?? PeopleTaggingBackfillState.Ready;
}

export const PeopleManagement: FunctionComponent = () => {
  const { updateFilter, updatePage, updateShowHiddenPeople, state } = usePeopleManagementContext();
  const peopleTaggingUiState = getPeopleTaggingUiState();
  const isReady = peopleTaggingUiState === PeopleTaggingBackfillState.Ready;
  const {
    initialLoadingState,
    peopleData,
    peopleDataLoading,
    allCount,
    namedCount,
    unnamedCount,
    totalCount,
    totalPages,
    currentPage,
    appliedSearch,
  } = useFetchPeopleHubData({ enabled: isReady });

  const [localPeopleData, setLocalPeopleData] = useState<PeoplePage[]>([]);
  const peopleHubPageLoadTrackedRef = useRef(false);
  const peopleHubSearchExecuteBaselineRef = useRef<{
    filter: PeopleFilterType;
    search: string;
    sort: string;
  } | null>(null);
  const peopleHubSearchExecuteSkipFirstRef = useRef(true);

  useEffect(() => {
    setLocalPeopleData(peopleData);
  }, [peopleData]);

  useEffect(() => {
    if (peopleHubPageLoadTrackedRef.current || initialLoadingState || peopleDataLoading) {
      return;
    }
    peopleHubPageLoadTrackedRef.current = true;

    if (typeof window === 'undefined' || !window.BFG?.hasFeature?.('people_tagging')) {
      return;
    }
    if (!amplitudeService.isReady()) {
      return;
    }

    const eventArea = 'AppFrontend';
    const eventName = 'PageLoad';
    const productArea = 'BulkManagement';
    const featureArea = 'PeopleHub';
    const elementLocation = 'PeopleHubPage';
    const elementName = 'PeopleHubView';
    const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;

    amplitudeService.trackEvent(eventName, {
      eventArea,
      productArea,
      featureArea,
      elementLocation,
      elementName,
      eventName,
      eventFull,
      brandfolderId: window.BFG?.brandfolder_key ?? undefined,
      organizationId: window.BFG?.organization_key ?? undefined,
      userId: window.BFG?.currentUser?.user_id ?? undefined,
      userRole: window.BFG?.currentUser?.role ?? undefined,
      totalIdentities: totalCount,
      namedIdentities: namedCount,
      unnamedIdentities: unnamedCount,
      entryPoint: readPeopleHubEntryPoint(),
    });
  }, [initialLoadingState, peopleDataLoading, totalCount, namedCount, unnamedCount]);

  useEffect(() => {
    if (initialLoadingState || peopleDataLoading) {
      return;
    }

    const snapshot = {
      filter: state.filter,
      sort: state.sort,
      search: appliedSearch,
    };

    if (peopleHubSearchExecuteSkipFirstRef.current) {
      peopleHubSearchExecuteSkipFirstRef.current = false;
      peopleHubSearchExecuteBaselineRef.current = snapshot;
      return;
    }

    const baseline = peopleHubSearchExecuteBaselineRef.current;
    if (
      baseline &&
      baseline.filter === snapshot.filter &&
      baseline.sort === snapshot.sort &&
      baseline.search === snapshot.search
    ) {
      return;
    }

    peopleHubSearchExecuteBaselineRef.current = snapshot;

    const searchTrimmed = appliedSearch;
    if (window.BFG?.hasFeature?.('people_tagging') && amplitudeService.isReady()) {
      const eventArea = 'AppFrontend';
      const eventName = 'SearchExecute';
      const productArea = 'BulkManagement';
      const featureArea = 'PeopleHub';
      const elementName = 'FilterIdentities';
      const elementLocation = 'PeopleHubPage';
      const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
      amplitudeService.trackEvent(eventName, {
        eventArea,
        productArea,
        featureArea,
        elementLocation,
        elementName,
        eventName,
        eventFull,
        brandfolderId: window.BFG?.brandfolder_key ?? undefined,
        organizationId: window.BFG?.organization_key ?? undefined,
        userId: window.BFG?.currentUser?.user_id ?? undefined,
        userRole: window.BFG?.currentUser?.role ?? undefined,
        filterType: searchExecuteFilterType(state.filter, searchTrimmed),
        sortBy: state.sort,
        searchTermLength: searchTrimmed.length,
        resultCount: totalCount,
      });
    }
  }, [
    appliedSearch,
    initialLoadingState,
    peopleDataLoading,
    state.filter,
    state.sort,
    totalCount,
  ]);

  const handleLocalMerge = useCallback((
    sourceId: string,
    targetPerson: { person_id: string; name: string; thumbnail_url: string; facesMoved?: number }
  ) => {
    setLocalPeopleData((prev) => {
      const targetExists = prev.some((p) => p.person_id === targetPerson.person_id);

      if (targetExists) {
        return prev
          .filter((p) => p.person_id !== sourceId)
          .map((p) =>
            p.person_id === targetPerson.person_id && targetPerson.facesMoved
              ? { ...p, face_count: (p.face_count || 0) + targetPerson.facesMoved }
              : p
          );
      } else {
        return prev.map((p) =>
          p.person_id === sourceId
            ? {
              ...p,
              person_id: targetPerson.person_id,
              name: targetPerson.name,
              thumbnail_url: targetPerson.thumbnail_url
            }
            : p
        );
      }
    });
  }, []);

  const handleLocalNameUpdate = useCallback((personId: string, newName: string) => {
    setLocalPeopleData((prev) =>
      prev.map((p) => p.person_id === personId ? { ...p, name: newName } : p)
    );
  }, []);

  if (!isReady) {
    return (
      <I18nProviderWrapper>
        <div
          className="people-management-wrapper"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <PeoplePageHeader />
          <PeopleTaggingStatusScreen uiState={peopleTaggingUiState} />
        </div>
      </I18nProviderWrapper>
    );
  }

  const showEmptyState =
    !initialLoadingState && !peopleDataLoading && totalCount === 0;

  if (showEmptyState) {
    return (
      <I18nProviderWrapper>
        <div
          className="people-management-wrapper"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <PeoplePageHeader />
          <NoFacesEmptyState />
        </div>
      </I18nProviderWrapper>
    );
  }

  return (
    <I18nProviderWrapper>
      <div className="people-management-wrapper">
        <PeoplePageHeader />
        <PeopleManagementActionsBar />
        <PeopleManagementFilter
          activeFilter={state.filter}
          allCount={allCount}
          namedCount={namedCount}
          onFilterChange={updateFilter}
          unnamedCount={unnamedCount}
        />
        <PeopleManagementContent
          currentPage={currentPage}
          initialLoadingState={initialLoadingState}
          onLocalMerge={handleLocalMerge}
          onLocalNameUpdate={handleLocalNameUpdate}
          onPageChange={updatePage}
          peopleData={localPeopleData}
          peopleDataLoading={peopleDataLoading}
          setShowHiddenPeople={updateShowHiddenPeople}
          showHiddenPeople={state.showHiddenPeople}
          totalCount={totalCount}
          totalPages={totalPages}
        />
      </div>
    </I18nProviderWrapper>
  );
};

export default (): JSX.Element => {
  return (
    <PeopleManagementProvider>
      <PeopleManagement />
    </PeopleManagementProvider>
  );
};
